import { useNewJoinersAndAllocationsKYCController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsKYC/useNewJoinersAndAllocationsKYCController';
import { HowTopProvideDocsModal } from '@dealstack/frontend/shared/components/HowToProvideDocs/HowTopProvideDocsModal';
import { PEPModal } from '@dealstack/frontend/shared/components/PEPModal/PEPModal';
import {
	CHIP_THEME,
	COLOR,
	Chips as ChipsV2,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import {
	SubTitle,
	SectionHeader,
} from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { Tooltip } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import {
	TransMipCreate,
	useTranslationMipCreate,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { memo } from 'react';
import { isAnyCertificationRequired } from '@dealstack/shared/lib/common/business/kycCertification';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import { NewJoinersAndAllocationsFormsTemplatesTable } from './NewJoinersAndAllocationsFormsTemplatesTable/NewJoinersAndAllocationsFormsTemplatesTable';
import { NewJoinersAndAllocationsProofToggles } from './NewJoinersAndAllocationsProofToggles';
import { ProgrammeProofsUnavailableAlert } from './ProgrammeProofsUnavailableAlert';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';

interface Props {
	className?: string;
}

const INDIVIDUALS_CHIPS = ['kyc.individuals.passport.title', 'kyc.individuals.address.title'];

const PIV_CHIPS = [
	{
		title: 'kyc.piv.share.title',
		tooltip: 'kyc.piv.share.tooltip',
	},
	{
		title: 'kyc.piv.registration.title',
		tooltip: 'kyc.piv.registration.tooltip',
	},
	{
		title: 'kyc.piv.association.title',
		tooltip: 'kyc.piv.association.tooltip',
	},
];

const FUNDS_CHIPS = [
	'kyc.list.funds.chips.salary',
	'kyc.list.funds.chips.inheritance',
	'kyc.list.funds.chips.investments',
	'kyc.list.funds.chips.personal',
	'kyc.list.funds.chips.others',
];

const INDIVIDUAL_PROOF_FLAGS = ['isProofOfIdentityRequired', 'isProofOfAddressRequired'] as const;
const VEHICLE_PROOF_FLAGS = ['isProofOfInvestmentVehicleRequired'] as const;

const NewJoinersAndAllocationsKyc = ({ className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const {
		kycForm,
		programmeProofsStatus,
		isPEPModalVisible,
		showHowToProvideDocsModal,
		setShowHowToProvideDocsModal,
		setPEPModalVisible,
	} = useNewJoinersAndAllocationsKYCController();
	const kycValue = kycForm.getValues();
	// Nothing was loaded, so the toggles are showing defaults rather than the programme's answer.
	const areProofsEditable = programmeProofsStatus === 'loaded';

	return (
		<div className={className}>
			<MainTitle
				size="h5b"
				title={tMipCreate('kyc.header.title')}
				subtitle={
					<Text variant="caption" colorFaded>
						<TransMipCreate
							i18nKey="kyc.header.subtitle"
							components={{
								clickLink: (
									<Text
										isLink
										bold
										colorLink
										onClick={() => setShowHowToProvideDocsModal(true)}
									/>
								),
							}}
						/>
					</Text>
				}
			/>
			<FormProvider {...kycForm}>
				{programmeProofsStatus === 'unavailable' && <ProgrammeProofsUnavailableAlert />}

				<SectionHeader
					size="p-lgb"
					title={tMipCreate('kyc.individuals.title')}
					subtitle={tMipCreate('kyc.individuals.subtitle')}
				/>

				<ChipsWrapper>
					{INDIVIDUALS_CHIPS.map((text) => (
						<ChipsV2
							prefixIcon="checkmark-circle-contained-filled"
							size="s"
							color={COLOR.GREEN}
							chipTheme={CHIP_THEME.LIGHT}
							key={text}
						>
							{tMipCreate(text)}
						</ChipsV2>
					))}
				</ChipsWrapper>
				<NewJoinersAndAllocationsProofToggles
					group="individualProofs"
					flags={INDIVIDUAL_PROOF_FLAGS}
					disabled={!areProofsEditable}
				/>

				<SectionHeader
					title={
						<Text variant="p-lg" bold>
							<TransMipCreate
								i18nKey="kyc.piv.title"
								components={{
									greyText: <Text bold={false} colorFaded />,
								}}
							/>
						</Text>
					}
					subtitle={tMipCreate('kyc.piv.subtitle')}
				/>

				<ChipsWrapper>
					{PIV_CHIPS.map((chip) => (
						<ChipsV2
							prefixIcon="checkmark-circle-contained-filled"
							suffixIcon={<Tooltip tooltip={tMipCreate(chip.tooltip)} />}
							key={chip.title}
							size="s"
							color={COLOR.GREEN}
							chipTheme={CHIP_THEME.LIGHT}
						>
							{tMipCreate(chip.title)}
						</ChipsV2>
					))}
				</ChipsWrapper>
				<NewJoinersAndAllocationsProofToggles
					group="vehicleProofs"
					flags={VEHICLE_PROOF_FLAGS}
					disabled={!areProofsEditable}
				/>

				<SectionHeader size="h5b" title={tMipCreate('kyc.forms.title')} />
				<SectionHeader
					size="h5b"
					title={tMipCreate('kyc.forms.individuals.title')}
					subtitle={tMipCreate('kyc.forms.individuals.subtitle')}
				/>
				<NewJoinersAndAllocationsFormsTemplatesTable type={CustomDetailEnum.INDIVIDUAL} />

				<SectionHeader
					size="h5b"
					title={tMipCreate('kyc.forms.piv.title')}
					subtitle={tMipCreate('kyc.forms.piv.subtitle')}
				/>
				<NewJoinersAndAllocationsFormsTemplatesTable type={CustomDetailEnum.VEHICLE} />

				<ListTitle size="h5b" title={tMipCreate('kyc.list.title')} />
				<List>
					<ListItem>
						<ListHeader title={tMipCreate('kyc.list.header.column1.title')} />
						<ListHeader
							title={tMipCreate('kyc.list.header.column2.title')}
							tooltip={tMipCreate('kyc.list.header.column2.tooltip')}
						/>
						<ListHeader
							title={tMipCreate('kyc.list.header.column3.title')}
							tooltip={tMipCreate('kyc.list.header.column3.tooltip')}
						/>
					</ListItem>

					<ListItem>
						<SectionHeader
							size="p-lgb"
							title={tMipCreate('kyc.list.beneficial.title')}
							subtitle={
								<Text variant="caption" colorFaded>
									<TransMipCreate i18nKey="kyc.list.beneficial.subtitle" />
								</Text>
							}
						/>
						<div>
							<ChipsV2
								prefixIcon="checkmark-circle-contained-filled"
								size="s"
								color={COLOR.GREEN}
								chipTheme={CHIP_THEME.LIGHT}
							>
								{tMipCreate('kyc.list.chips.individual')}
							</ChipsV2>
						</div>
						<div>
							<ChipsV2
								prefixIcon="checkmark-circle-contained-filled"
								size="s"
								color={COLOR.GREEN}
								chipTheme={CHIP_THEME.LIGHT}
							>
								{tMipCreate('kyc.list.chips.yes_no')}
							</ChipsV2>
						</div>
					</ListItem>

					<ListItem>
						<div>
							<Text variant="caption" colorFaded>
								<TransMipCreate
									i18nKey="kyc.list.beneficial.piv_subtitle"
									components={{ strong: <Text bold /> }}
								/>
							</Text>
						</div>

						<div>
							<ChipsV2
								prefixIcon="checkmark-circle-contained-filled"
								size="s"
								color={COLOR.GREEN}
								chipTheme={CHIP_THEME.LIGHT}
							>
								{tMipCreate('kyc.list.chips.piv')}
							</ChipsV2>
						</div>
						<div>
							<ChipsV2
								prefixIcon="checkmark-circle-contained-filled"
								size="s"
								color={COLOR.GREEN}
								chipTheme={CHIP_THEME.LIGHT}
							>
								{tMipCreate('kyc.list.chips.yes_no')}
							</ChipsV2>
						</div>
					</ListItem>

					<ListItem>
						<div>
							<SectionHeader
								size="p-lgb"
								title={tMipCreate('kyc.list.funds.title')}
								subtitle={tMipCreate('kyc.list.funds.subtitle')}
							/>
							<ChipsWrapper>
								{FUNDS_CHIPS.map((title) => (
									<ChipsV2
										prefixIcon="checkmark-circle-contained-filled"
										key={title}
										size="s"
										color={COLOR.GREY}
										chipTheme={CHIP_THEME.LIGHT}
									>
										{tMipCreate(title)}
									</ChipsV2>
								))}
							</ChipsWrapper>
						</div>

						<div>
							<ChipsV2
								prefixIcon="checkmark-circle-contained-filled"
								size="s"
								color={COLOR.GREEN}
								chipTheme={CHIP_THEME.LIGHT}
							>
								{tMipCreate('kyc.list.chips.all_investors')}
							</ChipsV2>
						</div>
						<div>
							<ChipsV2
								prefixIcon="checkmark-circle-contained-filled"
								size="s"
								color={COLOR.GREEN}
								chipTheme={CHIP_THEME.LIGHT}
							>
								{tMipCreate('kyc.list.chips.multiple_choice')}
							</ChipsV2>
						</div>
					</ListItem>

					<ListItem>
						<SectionHeader
							size="p-lgb"
							title={tMipCreate('kyc.list.PEP.title')}
							subtitle={
								<SubTitle variant="caption">
									<TransMipCreate
										components={{
											open: (
												<Text
													i18NextInline
													colorLink
													isLink
													bold
													variant="caption"
													onClick={() => setPEPModalVisible(true)}
												/>
											),
										}}
										i18nKey="kyc.list.PEP.subtitle"
									/>
								</SubTitle>
							}
						/>
						<div>
							<ChipsV2
								prefixIcon="checkmark-circle-contained-filled"
								size="s"
								color={COLOR.GREEN}
								chipTheme={CHIP_THEME.LIGHT}
							>
								{tMipCreate('kyc.list.chips.individual')}
							</ChipsV2>
						</div>
						<div>
							<ChipsV2
								prefixIcon="checkmark-circle-contained-filled"
								size="s"
								color={COLOR.GREEN}
								chipTheme={CHIP_THEME.LIGHT}
							>
								{tMipCreate('kyc.list.chips.yes_no')}
							</ChipsV2>
						</div>
					</ListItem>
				</List>
				{isPEPModalVisible ? <PEPModal onClose={() => setPEPModalVisible(false)} /> : null}
				{showHowToProvideDocsModal && (
					<HowTopProvideDocsModal
						requestCertifiedCopiesOfDocuments={isAnyCertificationRequired({
							...kycValue.individualProofs,
							...kycValue.vehicleProofs,
						})}
						onClose={() => setShowHowToProvideDocsModal(false)}
					/>
				)}
			</FormProvider>
		</div>
	);
};

const MainTitle = styled(SectionHeader)``;

const ChipsWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 1em;
`;

const ListTitle = styled(SectionHeader)``;
const ListHeader = styled(
	({ title, tooltip, className }: { title: string; tooltip?: string; className?: string }) => (
		<div className={className}>
			<Text variant="overline" colorFaded uppercase bold>
				{title}
			</Text>
			{!!tooltip && <Tooltip tooltip={tooltip} />}
		</div>
	)
)`
	display: flex;
	align-items: center;
	margin-bottom: 0.2em;
`;

const List = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: 4em;
`;

const ListItem = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	gap: 1em;
	margin-bottom: 1em;

	&:first-of-type {
		border-bottom: 1px solid ${({ theme }) => theme.v2.color.neutral[300]};
	}

	${ChipsWrapper} {
		margin-top: 1em;
	}

	& > div:first-of-type {
		flex-basis: 65%;
	}

	& > div:nth-of-type(2) {
		flex-basis: 15%;
	}
`;

const Memoized = memo(NewJoinersAndAllocationsKyc);
const Styled = styled(Memoized)`
	${SectionHeader} {
		margin: 1em 0;
		white-space: pre-line;
	}

	${ListTitle} {
		margin: 2em 0 0.5em 0;
	}

	${ListItem} ${SectionHeader} {
		margin: 0;
	}

	strong {
		font-weight: bold;
	}

	${NewJoinersAndAllocationsFormsTemplatesTable} {
		margin-bottom: 2em;
	}
`;

export { Styled as NewJoinersAndAllocationsKYC };
